import { ariaAttributeProps, dataAttributeProps } from '@pbk-web/utils';
import type { AriaRole, ChangeEvent, ClipboardEvent, FocusEvent, HTMLInputTypeAttribute, MouseEvent, ReactNode, Ref, RefAttributes } from 'react';
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';

import useSlot from '../../hooks/useSlots';
import type { SlotDefinitions } from '../../wrappers/withSlot';
import withSlot from '../../wrappers/withSlot';
import { InfoIcon, XIcon } from '../icons';
import RequiredStar from '../RequiredStar';
import Tooltip from '../tooltip/Tooltip';
import type { DynamicDataInterfaceHandle } from './DynamicDataInterfaceHandle';
import Error from './Error';

export interface InputProps {
  name?: string;
  value?: string;
  id?: string;
  type?: HTMLInputTypeAttribute;
  className?: string;
  wrapperClassName?: string;
  style?: InputStyle;
  placeholder?: string;
  label?: string;
  labelVisible?: boolean;
  textColor?: string;
  borderColor?: string;
  disabled?: boolean;
  readonly?: boolean;
  autocomplete?: string;
  autoFocus?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: KeyboardEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void; // react version of onKeyPress
  onClear?: () => void;
  error?: string;
  errorState?: boolean;
  min?: number;
  max?: number;
  maxLength?: number;
  /** Show the `maxLength` counter only while the input has focus. */
  counterOnFocus?: boolean;
  innerRef?: Ref<HTMLInputElement | null>;
  onPaste?: (event: ClipboardEvent) => void;
  helpText?: string;
  required?: boolean;
  /** Overrides the implicit textbox role, e.g. `combobox` for a search that drives a listbox. */
  role?: AriaRole;
}

export enum InputStyle {
  COMPOUND,
  NORMAL,
  MINIMAL,
  PLAIN,
}

const fallbackAutoComplete = 'auto';

const InputComponent = forwardRef<DynamicDataInterfaceHandle, InputProps & { children?: ReactNode; _slots: 'leading' | 'trailing' }>(
  function Input(props, ref) {
    const {
      id,
      name,
      value,
      type = 'text',
      borderColor = 'border-primary-100',
      textColor = 'text-primary-100',
      onChange,
      onFocus,
      onBlur,
      onClear,
      placeholder = '',
      label,
      className,
      style,
      disabled,
      readonly,
      autocomplete,
      onKeyPress,
      autoFocus,
      error,
      errorState,
      min,
      max,
      innerRef,
      maxLength,
      counterOnFocus,
      onPaste,
      wrapperClassName,
      helpText,
      required,
      onKeyDown,
      role,
    } = props;

    const [hasFocus, setHasFocus] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const hasLabel = label && label.length > 0;
    // Always an id, so the visible label and the error can be tied to the input.
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const callerAria = ariaAttributeProps(props);

    const inputHasLabelClasses = useMemo(() => {
      if (!hasLabel) return '';
      return style === InputStyle.MINIMAL ? 'mt-8' : 'mt-11';
    }, [hasLabel, style]);
    const inputStyleClasses = useMemo(() => {
      let result = `w-full ${disabled || readonly ? 'text-text-2' : textColor} placeholder-text-2 `;

      switch (style) {
        case InputStyle.COMPOUND:
          result += 'px-2 py-2 my-0 outline-none rounded-lg bg-white';
          break;

        case InputStyle.MINIMAL:
          result += 'px-2 py-1 outline-none rounded-lg text-pbk-14 bg-white';
          break;

        case InputStyle.PLAIN:
          result += 'px-1 py-1 outline-none rounded-lg bg-transparent';
          break;

        case InputStyle.NORMAL:
        default:
          result += 'px-2 py-2 outline-none rounded-lg bg-white';
          break;
      }

      return result;
    }, [disabled, readonly, style, textColor]);

    const wrapperStyleClasses = useMemo(() => {
      return `border-2 ${errorState ? 'border-red-500' : ''} ${
        hasFocus && !readonly && !errorState && style !== InputStyle.PLAIN
          ? 'border-accent-1-100'
          : disabled
            ? 'border-greyscale-15 text-text-2'
            : borderColor
      } rounded-lg`;
    }, [errorState, hasFocus, readonly, style, borderColor, disabled]);

    const onInputFocus = (event: FocusEvent<HTMLInputElement>) => {
      setHasFocus(true);
      onFocus && onFocus(event);
    };

    const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
      setHasFocus(false);
      onBlur && onBlur(event);
    };

    useEffect(() => {
      if (!onKeyPress) {
        return;
      }

      const handler = (event: KeyboardEvent) => {
        onKeyPress(event);
      };
      const ref = containerRef.current;

      ref?.addEventListener('keydown', handler);

      return () => {
        ref?.removeEventListener('keydown', handler);
      };
    }, [onKeyPress]);

    const slotClick = useCallback((e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      inputRef.current?.focus();
    }, []);

    const assignRef = useCallback(
      (ref: HTMLInputElement | null) => {
        if (!ref) {
          return;
        }
        if (typeof innerRef === 'function') {
          (innerRef as (instance: HTMLInputElement | null) => void)(ref);
        } else if (innerRef) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (innerRef.current as any) = ref;
        }
        inputRef.current = ref;
      },
      [innerRef],
    );

    const leadingSlot = useSlot(props, 'leading');
    const trailingSlot = useSlot(props, 'trailing');

    const [savedPosition, setSavedPosition] = useState<number | null>(null);
    useImperativeHandle(
      ref,
      () => ({
        savePosition: () => {
          setSavedPosition(inputRef.current?.selectionStart ?? 0);
        },
        insertPlaceholder: (placeholder: string) => {
          if (savedPosition === null) return;

          let result = value || '';
          result = `${result.slice(0, savedPosition)}${placeholder}${result.slice(savedPosition + 1)}`; // +1 to remove the `/` character
          onChange?.({ target: { value: result } } as unknown as ChangeEvent<HTMLInputElement>);
        },
      }),
      [onChange, savedPosition, value],
    );

    return (
      <div
        className={`relative w-full ${inputHasLabelClasses} ${disabled ? 'opacity-50' : ''}`}
        ref={containerRef}
        data-testid="input-wrapper"
        data-has-label={!!label}
      >
        {label && label.length > 0 && (
          <div className={`absolute top-0 left-0 ${helpText ? '-mt-9' : style === InputStyle.MINIMAL ? '-mt-5' : '-mt-7'}`}>
            <div className="flex items-center gap-2">
              <label htmlFor={inputId} className={`transition-opacity duration-150 ease-in-out ${style === InputStyle.MINIMAL ? 'text-pbk-14' : ''}`}>
                {required && <RequiredStar />}
                {label}
              </label>
              {!!helpText && (
                <Tooltip text={helpText}>
                  {(tooltip) => (
                    <div {...tooltip}>
                      <InfoIcon className="h-4 w-4" />
                    </div>
                  )}
                </Tooltip>
              )}
            </div>
          </div>
        )}
        <div className={`flex cursor-text items-center ${wrapperStyleClasses} ${wrapperClassName} bg-white`}>
          {/* Only used for mis-clicks on the input */}
          {}
          <div className="flex shrink-0 items-center justify-center overflow-hidden pl-2 empty:hidden" onMouseDown={slotClick}>
            {leadingSlot()}
          </div>
          <input
            /* Default identity, placed BEFORE the spread so a caller-supplied `data-testid` overrides it. */
            data-testid="input-field"
            {...dataAttributeProps(props)}
            // `name` names the input only without a visible label; a caller's aria-* wins.
            aria-label={hasLabel ? undefined : name}
            {...callerAria}
            // A shown error always reads as invalid; a caller's value applies only without one.
            aria-invalid={errorState || error ? true : callerAria['aria-invalid']}
            // A caller's description is kept alongside the error, never instead of it.
            aria-describedby={[callerAria['aria-describedby'], error && errorId].filter(Boolean).join(' ') || undefined}
            id={inputId}
            ref={assignRef}
            name={name}
            className={`${inputStyleClasses} ${className}`}
            value={value}
            placeholder={placeholder}
            type={type}
            onChange={onChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            disabled={disabled}
            readOnly={readonly}
            autoComplete={autocomplete || name || fallbackAutoComplete}
            autoFocus={autoFocus}
            min={min}
            max={max}
            maxLength={maxLength}
            onPaste={onPaste}
            onKeyDown={onKeyDown}
            role={role}
          />
          {/* Only used for mis-clicks on the input */}
          {}
          <div className="flex shrink-0 items-center justify-center overflow-hidden empty:hidden" onMouseDown={slotClick}>
            {!!maxLength && (!counterOnFocus || hasFocus) && (
              <span className="text-text-2 text-pbk-14 px-2 pt-1">
                {value?.length}/{maxLength}
              </span>
            )}
            {trailingSlot() ||
              (!!onClear && (
                <XIcon className="text-text-2 disabled:text-text-2 placeholder-greyscale-15 mr-2 h-4 w-4" onClick={() => !disabled && onClear()} />
              ))}
          </div>
        </div>
        <Error id={errorId}>{error}</Error>
      </div>
    );
  },
);

export const Input = withSlot<InputProps & RefAttributes<DynamicDataInterfaceHandle>, SlotDefinitions<['leading', 'trailing']>>(InputComponent);
